import { supabase } from "@/integrations/supabase/client";
import { UserService } from "@/services/users/UserService";
import {
  DocumentFolder,
  DocumentFolderWithCount,
  CreateDocumentFolderInput,
  UpdateDocumentFolderInput,
} from "@/types/knowledge/document-folder";

export class FolderService {
  /**
   * Get all folders for the current user
   */
  static async getFolders(userId?: string): Promise<DocumentFolderWithCount[]> {
    try {
      const { data: { user } } = await UserService.getUser();
      const effectiveUserId = userId || user?.id;

      if (!effectiveUserId) {
        throw new Error("User not authenticated");
      }

      // Get folders with document counts
      const { data: folders, error } = await supabase
        .from("document_folders")
        .select(`
          *,
          documents:documents(count)
        `)
        .eq("user_id", effectiveUserId)
        .order("position", { ascending: true })
        .order("name", { ascending: true });

      if (error) {
        console.error("Error fetching folders:", error);
        throw error;
      }

      // Transform the data to include document counts
      const foldersWithCount = (folders || []).map((folder: any) => ({
        ...folder,
        document_count: folder.documents?.[0]?.count || 0,
        documents: undefined, // Remove the documents array from response
      }));

      return foldersWithCount;
    } catch (error) {
      console.error("Error in getFolders:", error);
      return [];
    }
  }

  /**
   * Get a single folder by ID
   */
  static async getFolder(folderId: string): Promise<DocumentFolder | null> {
    try {
      const { data: folder, error } = await supabase
        .from("document_folders")
        .select("*")
        .eq("id", folderId)
        .single();

      if (error) {
        console.error("Error fetching folder:", error);
        return null;
      }

      return folder;
    } catch (error) {
      console.error("Error in getFolder:", error);
      return null;
    }
  }

  /**
   * Create a new folder
   */
  static async createFolder(input: CreateDocumentFolderInput, userId?: string): Promise<DocumentFolder | null> {
    try {
      const { data: { user } } = await UserService.getUser();
      const effectiveUserId = userId || user?.id;

      if (!effectiveUserId) {
        throw new Error("User not authenticated");
      }

      // Get the highest position number to append the new folder at the end
      const { data: maxPositionFolder } = await supabase
        .from("document_folders")
        .select("position")
        .eq("user_id", effectiveUserId)
        .order("position", { ascending: false })
        .limit(1)
        .single();

      const nextPosition = (maxPositionFolder?.position || 0) + 1;

      const { data: folder, error } = await supabase
        .from("document_folders")
        .insert({
          name: input.name,
          color: input.color || "#3B82F6",
          icon: input.icon || "folder",
          position: input.position !== undefined ? input.position : nextPosition,
          user_id: effectiveUserId,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating folder:", error);
        throw error;
      }

      return folder;
    } catch (error) {
      console.error("Error in createFolder:", error);
      return null;
    }
  }

  /**
   * Update a folder
   */
  static async updateFolder(folderId: string, input: UpdateDocumentFolderInput): Promise<DocumentFolder | null> {
    try {
      const { data: folder, error } = await supabase
        .from("document_folders")
        .update(input)
        .eq("id", folderId)
        .select()
        .single();

      if (error) {
        console.error("Error updating folder:", error);
        throw error;
      }

      return folder;
    } catch (error) {
      console.error("Error in updateFolder:", error);
      return null;
    }
  }

  /**
   * Delete a folder
   * Documents in the folder will have their folder_id set to NULL (due to ON DELETE SET NULL)
   */
  static async deleteFolder(folderId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("document_folders")
        .delete()
        .eq("id", folderId);

      if (error) {
        console.error("Error deleting folder:", error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error("Error in deleteFolder:", error);
      return false;
    }
  }

  /**
   * Reorder folders by updating their positions
   */
  static async reorderFolders(folderIds: string[]): Promise<boolean> {
    try {
      // Update each folder's position based on its index in the array
      const updates = folderIds.map((folderId, index) =>
        supabase
          .from("document_folders")
          .update({ position: index })
          .eq("id", folderId)
      );

      await Promise.all(updates);
      return true;
    } catch (error) {
      console.error("Error reordering folders:", error);
      return false;
    }
  }

  /**
   * Move a document to a folder
   */
  static async moveDocumentToFolder(documentId: string, folderId: string | null): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("documents")
        .update({ folder_id: folderId })
        .eq("id", documentId);

      if (error) {
        console.error("Error moving document to folder:", error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error("Error in moveDocumentToFolder:", error);
      return false;
    }
  }

  /**
   * Move multiple documents to a folder
   */
  static async moveDocumentsToFolder(documentIds: string[], folderId: string | null): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("documents")
        .update({ folder_id: folderId })
        .in("id", documentIds);

      if (error) {
        console.error("Error moving documents to folder:", error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error("Error in moveDocumentsToFolder:", error);
      return false;
    }
  }

  /**
   * Get documents count for a folder
   */
  static async getFolderDocumentCount(folderId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from("documents")
        .select("*", { count: "exact", head: true })
        .eq("folder_id", folderId)
        .eq("is_active", true);

      if (error) {
        console.error("Error getting folder document count:", error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error("Error in getFolderDocumentCount:", error);
      return 0;
    }
  }
}

